import React, { useState, useEffect } from 'react';
import { superAdminService } from '../../services/api';

export default function AnnouncementsManager() {
    const [announcements, setAnnouncements] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    // Form state
    const [title, setTitle] = useState('');
    const [message, setMessage] = useState('');
    const [type, setType] = useState('info');
    const [emailAll, setEmailAll] = useState(false);

    useEffect(() => {
        fetchAnnouncements();
    }, []);

    const fetchAnnouncements = async () => {
        setLoading(true);
        try {
            const res = await superAdminService.getAnnouncements();
            setAnnouncements(res.data.data || []);
        } catch (error) {
            console.error('Failed to load announcements', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!emailAll || window.confirm('Are you sure you want to broadcast this announcement via EMAIL to ALL registered users? This action cannot be undone.')) {
            setSubmitting(true);
            try {
                await superAdminService.createAnnouncement({ title, message, type, emailAll });
                setTitle('');
                setMessage('');
                setType('info');
                setEmailAll(false);
                fetchAnnouncements();
                alert('Announcement published successfully.' + (emailAll ? ' Email blast initiated.' : ''));
            } catch (error: any) {
                console.error('Creation failed', error);
                alert('Failed to publish announcement: ' + (error.response?.data?.message || error.message));
            } finally {
                setSubmitting(false);
            }
        }
    };

    const handleDelete = async (id: string) => {
        if (window.confirm('Delete this announcement?')) {
            try {
                await superAdminService.deleteAnnouncement(id);
                fetchAnnouncements();
            } catch (error) {
                console.error('Delete failed', error);
                alert('Failed to delete announcement.');
            }
        }
    };

    const handleToggle = async (id: string, active: boolean) => {
        try {
            await superAdminService.toggleAnnouncement(id, active);
            fetchAnnouncements();
        } catch (error) {
            console.error('Toggle failed', error);
            alert('Failed to toggle announcement status.');
        }
    };

    return (
        <div className="space-y-8">
            {/* Create Form */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
                <div className="bg-gray-900 px-6 py-5">
                    <h3 className="text-white font-bold text-lg">Broadcast New Announcement</h3>
                    <p className="text-gray-400 text-sm">Create a banner alert visible to everyone on their dashboard.</p>
                </div>
                <form onSubmit={handleCreate} className="p-6 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-1 md:col-span-2">
                            <label className="text-sm font-bold text-gray-700">Headline</label>
                            <input
                                required
                                type="text"
                                maxLength={100}
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                                placeholder="E.g., Scheduled Maintenance"
                            />
                        </div>

                        <div className="space-y-1 md:col-span-2">
                            <label className="text-sm font-bold text-gray-700">Detailed Message</label>
                            <textarea
                                required
                                maxLength={500}
                                rows={3}
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 resize-none"
                                placeholder="Details about this announcement..."
                            ></textarea>
                        </div>

                        <div className="space-y-1">
                            <label className="text-sm font-bold text-gray-700">Alert Type</label>
                            <select
                                value={type}
                                onChange={(e) => setType(e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                            >
                                <option value="info">Informational (Blue)</option>
                                <option value="maintenance">Maintenance (Purple)</option>
                                <option value="warning">Warning (Orange)</option>
                                <option value="critical">Critical (Red)</option>
                            </select>
                        </div>

                        <div className="flex items-center space-x-3 mt-6">
                            <input
                                type="checkbox"
                                id="emailAll"
                                checked={emailAll}
                                onChange={(e) => setEmailAll(e.target.checked)}
                                className="h-5 w-5 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                            />
                            <label htmlFor="emailAll" className="text-sm font-bold text-red-600 flex items-center">
                                <i className="fas fa-envelope mr-2"></i>
                                Email this alert to ALL users
                            </label>
                        </div>
                    </div>

                    <div className="flex justify-end pt-4 border-t border-gray-100 mt-4">
                        <button
                            type="submit"
                            disabled={submitting}
                            className={`px-6 py-2 rounded-lg text-white font-bold tracking-wide transition-all shadow-md flex items-center ${submitting ? 'bg-red-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'}`}
                        >
                            {submitting ? (
                                <><i className="fas fa-spinner fa-spin mr-2"></i> Publishing...</>
                            ) : (
                                <><i className="fas fa-bullhorn mr-2"></i> Publish Broadcast</>
                            )}
                        </button>
                    </div>
                </form>
            </div>

            {/* List Active/History */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
                <div className="bg-gray-50 px-6 py-5 border-b border-gray-100">
                    <h3 className="text-gray-900 font-bold text-lg">Announcement History</h3>
                </div>
                {loading ? (
                    <div className="p-8 text-center text-gray-500">
                        <i className="fas fa-spinner fa-spin text-2xl mb-2"></i>
                        <p>Loading history...</p>
                    </div>
                ) : (
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status & Type</th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Message</th>
                                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {announcements.map((ann) => (
                                <tr key={ann.id} className={ann.active ? 'bg-red-50/20' : 'opacity-60'}>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex flex-col space-y-2">
                                            <span className={`inline-flex px-2 py-1 text-xs font-bold rounded-md w-max ${ann.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'}`}>
                                                {ann.active ? 'LIVE' : 'EXPIRED'}
                                            </span>
                                            <span className="text-xs text-gray-500 font-mono uppercase border border-gray-200 bg-white px-2 py-0.5 rounded w-max">
                                                {ann.type}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="font-bold text-gray-900">{ann.title}</div>
                                        <div className="text-sm text-gray-500 mt-1 max-w-lg">{ann.message}</div>
                                        <div className="text-xs text-gray-400 mt-2">
                                            Created: {new Date(ann.createdAt).toLocaleString()} by {ann.createdBy}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                                        <button
                                            onClick={() => handleToggle(ann.id, !ann.active)}
                                            className={`${ann.active ? 'text-orange-500 hover:text-orange-700 bg-orange-50 border-orange-100' : 'text-green-500 hover:text-green-700 bg-green-50 border-green-100'} px-3 py-1.5 rounded-lg transition-colors border`}
                                            title={ann.active ? "Deactivate Announcement" : "Reactivate Announcement"}
                                        >
                                            <i className={`fas ${ann.active ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                        </button>
                                        <button
                                            onClick={() => handleDelete(ann.id)}
                                            className="text-red-500 hover:text-red-700 bg-red-50 px-3 py-1.5 rounded-lg transition-colors border border-red-100"
                                            title="Delete permanently"
                                        >
                                            <i className="fas fa-trash-alt"></i>
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {announcements.length === 0 && (
                                <tr>
                                    <td colSpan={3} className="px-6 py-8 text-center text-gray-500">
                                        No announcements found.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
